import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle, Info, XCircle, X } from 'lucide-react';

interface AlertProps {
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  onClose?: () => void;
  duration?: number;
  position?: 'top' | 'bottom';
  variant?: 'toast' | 'inline';
  actions?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  }[];
  id?: string;
  role?: 'alert' | 'status';
  testId?: string;
}

const alertStyles = {
  success: {
    bg: 'bg-success bg-opacity-10',
    border: 'border-success',
    text: 'text-success',
    icon: CheckCircle,
    role: 'status',
  },
  error: {
    bg: 'bg-error bg-opacity-10',
    border: 'border-error',
    text: 'text-error',
    icon: XCircle,
    role: 'alert',
  },
  warning: {
    bg: 'bg-warning bg-opacity-10',
    border: 'border-warning',
    text: 'text-warning',
    icon: AlertCircle,
    role: 'alert',
  },
  info: {
    bg: 'bg-primary bg-opacity-10',
    border: 'border-primary',
    text: 'text-primary',
    icon: Info,
    role: 'status',
  },
};

export const Alert: React.FC<AlertProps> = ({ type, title, message, onClose, duration = 1000, position = 'bottom', variant = 'inline', actions = [], id, role, testId }) => {
  const [isVisible, setIsVisible] = useState(true);
  const style = alertStyles[type];
  const Icon = style.icon;

  // Auto-dismiss toast alerts after duration
  useEffect(() => {
    if (variant === 'toast' && duration && onClose) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        // Delay actual close to allow exit animation to complete
        setTimeout(onClose, 300);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose, variant]);

  // Handle escape key to dismiss
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        setIsVisible(false);
        setTimeout(onClose, 300);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle close button click
  const handleClose = () => {
    setIsVisible(false);
    // Delay actual close to allow exit animation to complete
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  const alertContent = (
    <motion.div
      initial={{ opacity: 0, y: position === 'top' ? -20 : 20, scale: 0.95 }}
      animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : position === 'top' ? -20 : 20, scale: isVisible ? 1 : 0.95 }}
      exit={{ opacity: 0, y: position === 'top' ? -20 : 20, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className={`
        ${style.bg} border ${style.border}
        border rounded-lg p-2.5 sm:p-4
        ${variant === 'toast' ? 'shadow-lg' : ''}
        flex items-start justify-between
        w-full max-w-[90vw] sm:max-w-xs md:max-w-md
      `}
      role={role || style.role}
      aria-live={type === 'error' || type === 'warning' ? 'assertive' : 'polite'}
      data-testid={testId}
      id={id}
    >
      <div className="flex items-start flex-1">
        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${style.text} mt-0.5 flex-shrink-0`} aria-hidden="true" />
        <div className="mr-2 sm:mr-3 rtl:ml-2 sm:rtl:ml-3 rtl:mr-0 flex-1">
          {title && <h3 className={`text-xs sm:text-sm font-medium ${style.text}`}>{title}</h3>}
          <div className={`${title ? 'mt-1 sm:mt-2' : ''} text-xs sm:text-sm text-gray-600`}>{message}</div>

          {actions.length > 0 && (
            <div className="mt-3 flex space-x-2 rtl:space-x-reverse">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`
                    text-sm px-3 py-1.5 rounded-md
                    ${action.variant === 'secondary' ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : `${style.bg} ${style.text} hover:bg-opacity-20`}
                    transition-colors duration-200
                  `}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {onClose && (
        <button
          onClick={handleClose}
          className={`
            ${style.text} hover:bg-gray-100
            rounded-full p-1 transition-colors duration-200
            -mt-1 -mr-1 rtl:mr-auto rtl:-ml-1
            focus:outline-none focus:ring-2 focus:ring-offset-2 ${style.border}
          `}
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </motion.div>
  );

  if (variant === 'toast') {
    return <AnimatePresence>{isVisible && alertContent}</AnimatePresence>;
  }

  return <AnimatePresence>{isVisible && alertContent}</AnimatePresence>;
};
